import React, { useState, useEffect } from 'react';
import { useEntity } from '../contexts/EntityContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Euro, 
  FileText, 
  Receipt,
  Plus,
  ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { API_URL } from '../config/api';

export function Dashboard() {
  const { user } = useAuth();
  const { selectedEntity, isDutchEntity, isUSEntity } = useEntity();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [statements, setStatements] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedEntity) {
      fetchData();
    }
  }, [selectedEntity]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch financial statements
      const stmtRes = await fetch(
        `${API_URL}/api/financial-statements/${selectedEntity.entity_id}`,
        { credentials: 'include' }
      );
      if (stmtRes.ok) {
        const data = await stmtRes.json();
        setStatements(data);
      }

      // Fetch recent transactions
      const txnRes = await fetch(
        `${API_URL}/api/transactions?entity_id=${selectedEntity.entity_id}`,
        { credentials: 'include' }
      );
      if (txnRes.ok) {
        const data = await txnRes.json();
        setRecentTransactions(data.slice(0, 5));
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat(isDutchEntity ? 'nl-NL' : 'en-US', {
      style: 'currency',
      currency: currency || (isDutchEntity ? 'EUR' : 'USD')
    }).format(amount || 0);
  };

  const CurrencyIcon = isDutchEntity ? Euro : DollarSign;
  const currency = isDutchEntity ? 'EUR' : 'USD';

  if (!selectedEntity) {
    return (
      <div className="animate-fade-in">
        <div className="text-center py-12">
          <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {isDutchEntity ? 'Welkom bij GlobalLedger' : 'Welcome to GlobalLedger'}
          </h2>
          <p className="text-muted-foreground mb-6">
            {isDutchEntity 
              ? 'Voeg eerst een entiteit toe om te beginnen.'
              : 'Add an entity first to get started.'}
          </p>
          <Button 
            data-testid="add-entity-btn"
            onClick={() => navigate('/settings')}
            className="btn-brand"
          >
            <Plus className="w-4 h-4 mr-2" />
            {isDutchEntity ? 'Entiteit Toevoegen' : 'Add Entity'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-8">
      {/* Header */}
      <div>
        <h1 
          className="text-3xl font-bold tracking-tight"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-muted-foreground mt-1">
          {selectedEntity.name} - {t('dashboard.overview')}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="dashboard-grid">
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('dashboard.totalIncome')}</p>
                <p className="kpi-value text-emerald-600" data-testid="total-income">
                  {formatCurrency(statements?.income_statement?.total_income, currency)}
                </p>
              </div>
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('dashboard.totalExpenses')}</p>
                <p className="kpi-value text-red-600" data-testid="total-expenses">
                  {formatCurrency(statements?.income_statement?.total_expenses, currency)}
                </p>
              </div>
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('dashboard.netIncome')}</p>
                <p 
                  className={`kpi-value ${(statements?.income_statement?.net_income || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}
                  data-testid="net-income"
                >
                  {formatCurrency(statements?.income_statement?.net_income, currency)}
                </p>
              </div>
              <div className="w-10 h-10 bg-[hsl(var(--brand))]/10 rounded-lg flex items-center justify-center">
                <CurrencyIcon className="w-5 h-5 text-[hsl(var(--brand))]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('dashboard.openInvoices')}</p>
                <p className="kpi-value text-amber-600" data-testid="open-invoices">
                  {formatCurrency(statements?.balance_sheet?.assets?.accounts_receivable, currency)}
                </p>
              </div>
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Button 
          variant="outline" 
          className="h-auto py-4 justify-start"
          data-testid="quick-add-transaction"
          onClick={() => navigate('/bookkeeping')}
        >
          <Plus className="w-5 h-5 mr-3 text-[hsl(var(--brand))]" />
          <span>{isDutchEntity ? 'Transactie Toevoegen' : 'Add Transaction'}</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-4 justify-start"
          data-testid="quick-new-invoice"
          onClick={() => navigate('/invoices')}
        >
          <FileText className="w-5 h-5 mr-3 text-[hsl(var(--brand))]" />
          <span>{isDutchEntity ? 'Factuur Maken' : 'Create Invoice'}</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-4 justify-start"
          data-testid="quick-upload-statement"
          onClick={() => navigate('/bank-statements')}
        >
          <Receipt className="w-5 h-5 mr-3 text-[hsl(var(--brand))]" />
          <span>{isDutchEntity ? 'Bankafschrift Uploaden' : 'Upload Statement'}</span>
        </Button>
        {isDutchEntity ? (
          <Button 
            variant="outline" 
            className="h-auto py-4 justify-start"
            data-testid="quick-vat-return"
            onClick={() => navigate('/vat-returns')}
          >
            <Receipt className="w-5 h-5 mr-3 text-[hsl(var(--brand))]" />
            <span>BTW Aangifte</span>
          </Button>
        ) : (
          <Button 
            variant="outline" 
            className="h-auto py-4 justify-start"
            data-testid="quick-tax-instructions"
            onClick={() => navigate('/tax-instructions')}
          >
            <Receipt className="w-5 h-5 mr-3 text-[hsl(var(--brand))]" />
            <span>Tax Instructions</span>
          </Button>
        )}
      </div>

      {/* Recent Transactions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('dashboard.recentTransactions')}
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/bookkeeping')}
          >
            {isDutchEntity ? 'Alles bekijken' : 'View all'}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-12 bg-muted animate-pulse rounded" />
              ))}
            </div>
          ) : recentTransactions.length > 0 ? (
            <div className="space-y-2">
              {recentTransactions.map(txn => (
                <div 
                  key={txn.transaction_id}
                  className="flex items-center justify-between py-3 border-b border-border last:border-0"
                >
                  <div>
                    <p className="font-medium text-sm">{txn.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(txn.date).toLocaleDateString(isDutchEntity ? 'nl-NL' : 'en-US')}
                      {txn.vat_applicable && (
                        <span className="ml-2 vat-indicator vat-applicable">
                          {isDutchEntity ? `${txn.vat_rate || 21}% BTW` : `${txn.vat_rate || 0}% Tax`}
                        </span>
                      )}
                    </p>
                  </div>
                  <p className={`font-mono text-sm ${txn.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(txn.amount, txn.currency)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              {isDutchEntity ? 'Nog geen transacties' : 'No transactions yet'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Bank Integration Info */}
      <Card className="border-dashed">
        <CardContent className="py-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
              {isDutchEntity ? (
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/d1/ABN_AMRO_logo.svg/200px-ABN_AMRO_logo.svg.png" 
                  alt="ABN AMRO" 
                  className="w-8 h-8 object-contain"
                />
              ) : (
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/20/Bank_of_America_logo.svg/200px-Bank_of_America_logo.svg.png" 
                  alt="Bank of America" 
                  className="w-8 h-8 object-contain"
                />
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {isDutchEntity ? 'ABN AMRO Integratie' : 'Bank of America Integration'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isDutchEntity 
                  ? 'Directe API-koppeling vereist PSD2 TPP-registratie. Upload voorlopig PDF-afschriften.'
                  : 'Direct API connection requires CashPro access. Upload PDF statements for now.'}
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/bank-statements')}>
              {isDutchEntity ? 'PDF Uploaden' : 'Upload PDF'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
